import { useRouter } from "next/router";

import { useTranslation } from "@flare/i18n";

import { i18n } from "@/services/locale/i18n";

import { ActionButton } from "../components/action-button";
import { ActionsContainer } from "../components/actions-container";
import { BackButton } from "../components/back-button";
import { Footer } from "../components/footer";
import { PrivacyDisclaimer } from "../components/privacy-disclaimer";
import { StepContainer } from "../components/step-container";
import { StepContent } from "../components/step-content";
import { StepTitle } from "../components/step-title";
import type { FunnelAnswers } from "../config";
import { funnelInputsNameMap } from "../config";

import { ChoiceBoxContent } from "./components/choice-box-content";
import type { ChoiceObject, PracticeList, SubPracticeList } from "./step.types";

export type MainGoalChoicesMap = {
  [k in PracticeList]?: {
    [k in SubPracticeList]?: ChoiceObject[];
  };
};

export default function MainGoal() {
  const { t } = useTranslation();
  const { query } = useRouter();
  const practice = query.practice as FunnelAnswers["practice"];
  const subPractice = query.subPractice as FunnelAnswers["subPractice"];
  const choices = getStepChoices({ practice, subPractice });

  return (
    <>
      <StepContainer>
        <StepContent>
          <BackButton />
          <StepTitle>{t("FUNNEL_MAIN_GOAL_TITLE")}</StepTitle>
          <ChoiceBoxContent
            name={funnelInputsNameMap.mainGoal}
            choices={choices}
            required
          />
        </StepContent>
        <ActionsContainer>
          <PrivacyDisclaimer />
          <ActionButton>{t("FUNNEL_ACTION_BUTTON_NEXT")}</ActionButton>
        </ActionsContainer>
      </StepContainer>
      <Footer />
    </>
  );
}

function getStepChoices({
  practice,
  subPractice,
}: {
  practice?: PracticeList;
  subPractice?: SubPracticeList;
}): ChoiceObject[] {
  if (!practice || !subPractice) {
    return [];
  }

  const choices: MainGoalChoicesMap = {
    family: {
      divorce: [
        {
          label: i18n.t("FAIR_DIVISION_OF_ASSETS"),
          value: "Fair division of assets",
        },
        {
          label: i18n.t("CHILD_CUSTODY_OR_SUPPORT"),
          value: "Child custody or support",
        },
        {
          label: i18n.t("ALIMONY_OR_SPOUSAL_SUPPORT"),
          value: "Alimony or spousal support",
        },
        {
          label: i18n.t("PROTECTING_FINANCIAL_SECURITY"),
          value: "Protecting financial security",
        },
        { label: i18n.t("SOMETHING_ELSE"), value: "Something else" },
      ],
      "child-custody": [
        {
          label: i18n.t("GETTING_THE_BEST_OUTCOME_FOR_MY_CHILD"),
          value: "Getting the best outcome for my child",
        },
        {
          label: i18n.t("CREATING_A_FAIR_PARENTING_PLAN"),
          value: "Creating a fair parenting plan",
        },
        {
          label: i18n.t("ADDRESSING_CHILD_SUPPORT"),
          value: "Addressing child support",
        },
        { label: i18n.t("SOMETHING_ELSE"), value: "Something else" },
      ],
      "child-support": [
        {
          label: i18n.t("PROVIDING_FOR_MY_CHILD_EVERYDAY_NEEDS"),
          value: "Providing for my child’s everyday needs",
        },
        {
          label: i18n.t("ESTABLISHING_A_FAIR_FINANCIAL_ARRANGEMENT"),
          value: "Establishing a fair financial arrangement",
        },
        {
          label: i18n.t("HOLDING_THE_OTHER_PARENT_ACCOUNTABLE"),
          value: "Holding the other parent accountable",
        },
        { label: i18n.t("SOMETHING_ELSE"), value: "Something else" },
      ],
    },
  };

  return choices[practice]?.[subPractice] || [];
}
